import { inject, InjectionToken, Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../core/h3ml/state/output-info';
import {
  fusionStoreToken,
  TaskWorkspaceStore,
} from '../../../core/h3ml/state/task-workspace-store';
import { FusionManager } from '../../../core/outputs/annotation/annotation-shared/fusion/fusion-manager';
import { FusionManagerFactory } from '../../../core/outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { AnnotationLayoutService } from '../../../core/outputs/annotation/annotation-shared/layout/annotation-layout.service';
import { ShellConnector } from '../../../core/outputs/annotation/annotation-shared/shell-connector';
import { AnnotationStateUndoService } from '../../../core/outputs/annotation/annotation-shared/state/root/annotation-state-undo.service';
import { ObjectlessToolbarKeyboardShortcutService } from '../../../core/outputs/annotation/annotation-shared/toolbars/static-toolbar/objectless-toolbar-keyboard-shortcut.service';
import { UndoService } from '../../../core/outputs/annotation/annotation-shared/toolbars/static-toolbar/undo.service';
import { BulkRemoveFrameAnnotationsService } from '../../../core/outputs/annotation/annotation-shared/utils/bulk-remove-frame-annotations.service';
import { FocusedAnnotationService } from '../../../core/outputs/annotation/annotation-shared/utils/focused-annotation.service';
import { InteractionModeService } from '../../../core/outputs/annotation/annotation-shared/utils/interaction-mode.service';
import { KeyframeKeyboardShortcutsService } from '../../../core/outputs/annotation/annotation-shared/utils/keyframe-keyboard-shortcuts.service';
import { KeyframeService } from '../../../core/outputs/annotation/annotation-shared/utils/keyframe.service';
import { ObjectlessFocusedShapeService } from '../../../core/outputs/annotation/annotation-shared/utils/objectless-focused-shape.service';
import { OutputKeyboardShortcutService } from '../../../core/outputs/annotation/annotation-shared/utils/output-keyboard-shortcut.service';
import { PushAndTrackService } from '../../../core/outputs/annotation/annotation-shared/utils/push-and-track.service';
import { SelectionService } from '../../../core/outputs/annotation/annotation-shared/utils/selection.service';
import { ShapeSelectionService } from '../../../core/outputs/annotation/annotation-shared/utils/shape-selection.service';
import { VectorToPointCloudShapeService } from '../../../core/outputs/annotation/annotation-shared/utils/vector-to-point-cloud-shape.service';
import { ZoomPanelService } from '../../../core/outputs/annotation/annotation-shared/zoom-panel/zoom-panel.service';
import { AttributeContextMenuService } from '../../../core/shared/context-menu/attribute-context-menu.service';
import { CanvasContextMenuService } from '../../../core/shared/context-menu/canvas-context-menu.service';
import { ContextMenuOptionsService } from '../../../core/shared/context-menu/context-menu-options.service';
import { ContextMenuService } from '../../../core/shared/context-menu/context-menu.service';
import { ShapeContextMenuService } from '../../../core/shared/context-menu/shape-context-menu.service';
import { ShapeListContextMenuService } from '../../../core/shared/context-menu/shape-list-context-menu.service';
import { TimelineContextMenuService } from '../../../core/shared/context-menu/timeline-context-menu.service';
import { AbstractFeedbackDialogService } from '../../../core/shared/feedback/abstract-feedback-dialog.service';
import { ClientFeedbackDialogService } from '../../../core/shared/feedback/client-feedback-dialog.service';
import { ClientFeedbackCrudService } from '../../../core/shared/feedback/crud/client-feedback-crud.service';
import { ShapeFeedbackCrudService } from '../../../core/shared/feedback/crud/shape-feedback-crud.service';
import { ShapeFeedbackDialogService } from '../../../core/shared/feedback/shape-feedback-dialog.service';
import { FloatingPanelService } from '../../../core/shared/misc-services/floating-panel.service';
import { ShapeAccessService } from '../../../core/shared/misc-services/shape-access.service';
import { ShapeGroupingService } from '../../../core/shared/misc-services/shape-grouping.service';
import { ShapeVisibilityService } from '../../../core/shared/misc-services/shape-visibility.service';
import { SubpointBindingService } from '../../../core/shared/misc-services/subpoint-binding.service';
import { AnnotationCopyService } from '../../../core/shared/shape-copy-and-paste/annotation-copy.service';
import { AnnotationPastingService } from '../../../core/shared/shape-copy-and-paste/annotation-pasting.service';
import { PasteValidationService } from '../../../core/shared/shape-copy-and-paste/paste-validation.service';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { ToolsHelperService } from '../../../cross-team-shared/misc-services/tools-helper.service';
import { ToolName } from '../../../cross-team-shared/tools-names';
import { ShapeGroupOrderState } from '../../../team-apis/content/content-connector';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { SidePanelDisplayService } from './side-panel/side-panel-display.service';
import {
  AbstractAnnotationStore,
  annotationStoreToken,
} from './state/root/abstract-annotation-store';
import {
  AbstractAnnotationStore2D,
  annotationStore2DToken,
} from './state/root/abstract-annotation-store-2d';
import { SensorRendererState } from './state/root/sensor-renderer-state';
import { ObjectDragAndDropService } from './toolbars/shape-list-and-tag-editor/annotation-list/object-drag-and-drop.service';
import { ShapeListPanelService } from './toolbars/shape-list-and-tag-editor/objectless-shape-list/objectless-shape-list/shape-list-panel.service';
import { ShapeFilterService } from './toolbars/shape-list-and-tag-editor/shape-filter/shape-filter.service';
import { TagService } from './utils/tag.service';

export interface OutputInfoState extends ShapeGroupOrderState {
  sensorRendererState: SensorRendererState;
  trainingShapes?: StrictRecord<number, ShapeState>;
  selectedTool?: { name: ToolName; mode?: string };
}

export const outputAnnotationInfoStoreToken = new InjectionToken<
  Store<OutputInfoState>
>('output annotation info store');

export const feedbackDialogService = new InjectionToken<
  AbstractFeedbackDialogService<any>
>('feedbackDialogService');

export const annotationProviders = [
  AnnotationLayoutService,
  AnnotationStateUndoService,
  AttributeContextMenuService,
  BulkRemoveFrameAnnotationsService,
  CanvasContextMenuService,
  ClientFeedbackCrudService,
  ClientFeedbackDialogService,
  ContextMenuOptionsService,
  ContextMenuService,
  FloatingPanelService,
  ObjectlessFocusedShapeService,
  FocusedAnnotationService,
  InteractionModeService,
  KeyframeKeyboardShortcutsService,
  KeyframeService,
  ObjectDragAndDropService,
  OutputKeyboardShortcutService,
  PasteValidationService,
  VectorToPointCloudShapeService,
  PushAndTrackService,
  SelectionService,
  ShapeSelectionService,
  SidePanelDisplayService,
  ShapeAccessService,
  ShapeContextMenuService,
  ShapeFeedbackCrudService,
  ShapeFeedbackDialogService,
  ShapeGroupingService,
  ShapeListContextMenuService,
  ShapeVisibilityService,
  ShapeFilterService,
  ShapeListPanelService,
  ShellConnector,
  { provide: shellConnectorToken, useExisting: ShellConnector },
  {
    provide: feedbackDialogService,
    deps: [TaskWorkspaceStore, Injector],
    useFactory: (store: TaskWorkspaceStore) =>
      store.state().feedbackType === 'shape'
        ? inject(ShapeFeedbackDialogService)
        : inject(ClientFeedbackDialogService),
  },
  SubpointBindingService,
  TagService,
  TimelineContextMenuService,
  ObjectlessToolbarKeyboardShortcutService,
  ToolsHelperService,
  ZoomPanelService,
  { provide: annotationStoreToken, useExisting: AbstractAnnotationStore },
  {
    provide: FusionManager,
    deps: [FusionManagerFactory, outputInfoStoreToken],
    useFactory: (
      fusionManagerFactory: FusionManagerFactory,
      outputStore: Store<OutputInfo>,
    ) => fusionManagerFactory.getManager(outputStore.state().output.id),
  },
  {
    provide: fusionStoreToken,
    deps: [FusionManager],
    useFactory: (manager: FusionManager) => manager.fusionStore,
  },
  {
    provide: UndoService,
    deps: [FusionManager],
    useFactory: (manager: FusionManager) => manager.undoService,
  },
  {
    provide: outputAnnotationInfoStoreToken,
    deps: [outputInfoStoreToken, TaskWorkspaceStore],
    useFactory: (
      outputInfoStore: Store<OutputInfo>,
      taskWorkspaceStore: TaskWorkspaceStore,
    ) =>
      taskWorkspaceStore('outputAnnotationInfo')(
        outputInfoStore.state().output.name,
      ),
  },
  AnnotationCopyService,
  AnnotationPastingService,
];

export const annotationProviders2D = [
  annotationProviders,
  { provide: annotationStore2DToken, useExisting: AbstractAnnotationStore2D },
];
